import BaseSquareLogo from "@components/BaseSquareLogo";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { SolanaEmailModalSWMSheet } from "@components/modals/SolanaEmailModalSWM";
import useBaseAuth from "@hooks/useBaseAuth";
import useLoginRegister from "@hooks/useLoginRegister";
import { fonts, theme } from "@theme";
import {
  BottomSheetPlainLayout,
  BottomSheetSubtitle,
  BottomSheetTitle,
} from "@utils/swmModal/components/BottomSheetPrimitives";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import { forwardRef, useCallback, useState } from "react";
import { ActivityIndicator } from "react-native";
import {
  type SheetAdapterRef,
  useBottomSheetManager,
} from "react-native-bottom-sheet-stack";

const MiniAppLoginModalSWM = ({ closeModal }: { closeModal: () => void }) => {
  const [isLoading, setIsLoading] = useState(false);
  const baseAuth = useBaseAuth();
  const bottomSheetManager = useBottomSheetManager();
  const { authenticate } = useLoginRegister(true);

  const handleLogin = useCallback(async () => {
    setIsLoading(true);

    try {
      const loginResult = await baseAuth.loginWithBase({});

      if (loginResult.success) {
        await authenticate(
          undefined,
          undefined,
          undefined,
          undefined,
          loginResult.firebaseAuthToken,
        );
        closeModal();
        return;
      }

      if (loginResult.error === "no-email") {
        setIsLoading(false);
        closeModal();
        bottomSheetManager.open(
          <SolanaEmailModalSWMSheet
            onEmailEntered={async (email) => {
              const newLoginResult = await baseAuth.loginWithBase({ email });

              if (newLoginResult.success) {
                // A failed sign-in is reported by useLoginRegister.
                await authenticate(
                  undefined,
                  undefined,
                  undefined,
                  undefined,
                  newLoginResult.firebaseAuthToken,
                ).catch(() => undefined);
              }
            }}
          />,
          {
            mode: "push",
          },
        );
        return;
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Mini app login error:", error);
      setIsLoading(false);
    }
  }, [authenticate, baseAuth, bottomSheetManager, closeModal]);

  return (
    <BottomSheetPlainLayout contentProps={{ gap: "m1", pb: "s3" }}>
      <LBox alignItems="center" gap="s3" px="l2">
        <LIcon name="logo_round_large" size={64} />
        <LBox gap="s2">
          <BottomSheetTitle>Welcome to Lissen</BottomSheetTitle>
          <BottomSheetSubtitle>
            Log in to access your personalized music experience.
          </BottomSheetSubtitle>
        </LBox>
      </LBox>

      <LPressable
        disabled={isLoading}
        onPress={handleLogin}
        sentryEventName="miniapp_login_modal_base_login_swm"
        style={{
          alignItems: "center",
          backgroundColor: theme.colors.black,
          borderColor: theme.colors.blackTranslucent10,
          borderRadius: theme.borderRadii.max,
          borderWidth: 1,
          flexDirection: "row",
          height: 50,
          justifyContent: "center",
          opacity: isLoading ? 0.6 : 1,
          width: "100%",
        }}
      >
        <LBox flexDirection="row" gap="s2" alignItems="center">
          {isLoading ? (
            <ActivityIndicator color={theme.colors.white} size="small" />
          ) : (
            <>
              <BaseSquareLogo size={18} color={theme.colors.white} />
              <LText
                color="white"
                fontFamily={fonts.medium}
                fontSize={17}
                lineHeight={20}
              >
                Log in with Base
              </LText>
            </>
          )}
        </LBox>
      </LPressable>

      <LPressable
        disabled={isLoading}
        onPress={closeModal}
        sentryEventName="miniapp_login_modal_dismiss_swm"
        style={{
          alignItems: "center",
          height: 44,
          justifyContent: "center",
          opacity: isLoading ? 0.5 : 1,
        }}
      >
        <LText color="blackTranslucent70" variant="bodyM2">
          No thanks
        </LText>
      </LPressable>
    </BottomSheetPlainLayout>
  );
};

export const MiniAppLoginModalSWMSheet = forwardRef<SheetAdapterRef>(
  function MiniAppLoginModalSWMSheet(_props, ref) {
    return (
      <AppModalSheet ref={ref}>
        {({ closeModal }) => (
          <MiniAppLoginModalSWM closeModal={closeModal} />
        )}
      </AppModalSheet>
    );
  },
);

export default MiniAppLoginModalSWM;
